import { AuthenticationStatusGetStream, setAuthStatus, setAuthStatusPleb } from "../store/slices/authSlice";
import { setStatusGetStream } from "../store/slices/authSlice";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../store";
import { useAuth } from "./useAuth";
import { useCallback, useEffect } from "react";
import { AuthenticationStatus } from "../store/slices/authSlice";
import { useStream } from "./useStream";
import { Repository } from "../repositories/Repository";
import { useWallet } from "./useWallet";
import { useTheme } from "./useTheme";
import { currentUserRepository } from "../repositories/CurrentUserRepository";
import mixpanel from "mixpanel-browser";
import { useConfirm } from "./useConfirm";
import { ButtonVariant } from "../config/colors";

export function useApp() {
    const dispatch = useDispatch<AppDispatch>();
    const {pleb, status, currentUserId, tokens, logout} = useAuth();
    const {loginGetStream, logoutGetStream} = useStream();
    const {initWallet, unsetWallet} = useWallet();
    const {initializeTheme, isDarkMode} = useTheme();
    const {openConfirm} = useConfirm();

    useEffect(() => {
        initializeTheme();
    }, [initializeTheme]);
    
    useEffect(() => {
        if (isDarkMode) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
    }, [isDarkMode]);

    const initCurrentUser = useCallback(async () => {
        Repository.onLogout = () => {
            logout();
        }

        if(tokens) {
            Repository.setCredentials(tokens);

        currentUserRepository.getProfile()
            .then((pleb) => {
                if(pleb.subscription)
                {
                    dispatch(setAuthStatusPleb({
                        status: AuthenticationStatus.AUTHENTICATED,
                        pleb: pleb?.toJson() ?? null,
                        tokens: tokens
                    }));
                    return;
                }
                else
                {
                    logout();
                    openConfirm({
                        message: "To use this app you must have an active subscription",
                        buttons: [
                            { 
                                label: "Sign up", 
                                href: process.env.REACT_APP_LINK_SIGNUP, 
                                target: "_blank", 
                                variant: ButtonVariant.primary 
                            },
                        ],
                    });
                }
            })
            .catch((error) => {
                console.error("error", error);
            });
        }
    }, [currentUserId, loginGetStream, dispatch]);

    useEffect(() => {
        switch(status) {
            case AuthenticationStatus.AUTHENTICATED:
                loginGetStream(pleb?.id ?? '')
                    .then(() => {
                        dispatch(setStatusGetStream(AuthenticationStatusGetStream.LOGGED));
                    })
                    .catch((error) => {
                        console.error('error login GetStream', error);
                        dispatch(setStatusGetStream(AuthenticationStatusGetStream.ERROR));
                    });

                if(Repository.credentials)
                    initWallet(Repository.credentials); // @todo : fix

                mixpanel.identify(pleb?.id ?? '');
                mixpanel.people.set({
                    name: pleb?.name,
                    slug: pleb?.slug,
                    email: pleb?.email
                });
                mixpanel.track('$ae_session');
                break;
            case AuthenticationStatus.LOGGING_OUT:
                Promise.all([
                    logoutGetStream(),
                    unsetWallet()
                ])
                    .then(() => {
                        dispatch(setAuthStatus(AuthenticationStatus.INITIAL));
                    })
                    .catch((error) => {
                        console.error('error logoutGetStream and unsetWallet', error);
                        dispatch(setAuthStatus(AuthenticationStatus.INITIAL));
                    })
                break;
            case AuthenticationStatus.INITIAL:
                if(currentUserId) {
                    initCurrentUser()
                }
                else {
                    dispatch(setAuthStatus(AuthenticationStatus.UNAUTHENTICATED));
                }
                break;
        }
    }, [status]);

    // to fix the height of the page on ios safari
    useEffect(() => {
        // Set CSS variable for viewport height
        const setVH = () => {
            const vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty('--vh', `${vh}px`);
        };
    
        // Initial set
        setVH();
    
        // Update on resize and orientation change
        window.addEventListener('resize', setVH);
        window.addEventListener('orientationchange', setVH);
    
        return () => {
        window.removeEventListener('resize', setVH);
        window.removeEventListener('orientationchange', setVH);
        };
    }, []);
}